import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { obtenerAplicacionFertilizante } from '../../services/aplicacionesFertilizantes';
import AppShell from '../../components/layout/AppShell';
import StatusBadge from '../../components/ui/StatusBadge';

const METODO_LABELS = { FERTIRRIEGO: 'Fertirriego', COBERTERA: 'Cobertera' };

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return fechaaplicacion;
  }
}

function formatFechaHora(valor) {
  if (!valor) return '—';
  try {
    return new Date(valor).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return valor;
  }
}

function formatHora(hora) {
  if (!hora) return '—';
  return hora.slice(0, 5);
}

function formatHoraRango(horainicio, horafin) {
  const inicio = formatHora(horainicio);
  const fin = formatHora(horafin);
  if (inicio === '—' && fin === '—') return '—';
  return `${inicio} – ${fin}`;
}

function FertilizanteDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const justCreated = Boolean(location.state?.justCreated);

  const [aplicacion, setAplicacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setLoading(true);
    setError('');
    obtenerAplicacionFertilizante(id).then((result) => {
      if (result.ok) {
        setAplicacion(result.aplicacion);
      } else {
        setError(result.message);
      }
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (loading) {
    return (
      <AppShell title="Aplicación de fertilizantes" backTo="/fertilizantes">
        <p className="text-center text-sm text-gray-500">Cargando...</p>
      </AppShell>
    );
  }

  if (error || !aplicacion) {
    return (
      <AppShell title="Aplicación de fertilizantes" backTo="/fertilizantes">
        <p className="text-center text-sm text-red-600">{error || 'Aplicación no encontrada.'}</p>
      </AppShell>
    );
  }

  const productos = aplicacion.aplicacion_fertilizante_detalle ?? [];
  const esActiva = aplicacion.estado === 'ACTIVA';

  return (
    <AppShell title="Aplicación de fertilizantes" backTo="/fertilizantes">
      <div className="flex flex-col gap-6">
        {justCreated && (
          <p className="rounded-lg bg-green-50 px-3 py-2 text-center text-sm font-medium text-green-700">
            Aplicación registrada correctamente
          </p>
        )}

        {!esActiva && (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="font-medium text-gray-900">Aplicación anulada</p>
            {aplicacion.motivo_anulacion && (
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-500">Motivo:</span> {aplicacion.motivo_anulacion}
              </p>
            )}
            <p className="mt-1 text-sm text-gray-600">
              <span className="font-medium text-gray-500">Fecha anulación:</span>{' '}
              {formatFechaHora(aplicacion.fecha_anulacion)}
            </p>
          </div>
        )}

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {formatFecha(aplicacion.fechaaplicacion)} ·{' '}
              {formatHoraRango(aplicacion.horainicio, aplicacion.horafin)}
            </h2>
            <StatusBadge estado={aplicacion.estado} />
          </div>

          <p className="mt-4 text-sm font-medium text-gray-500">Información general</p>
          <dl className="mt-2 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="font-medium text-gray-500">Productor</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.productores?.nombre || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Huerto</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.huerto || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Cuartel</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.cuartel || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Superficie (ha)</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.superficie_ha ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Hora inicio</dt>
              <dd className="mt-1 text-gray-900">{formatHora(aplicacion.horainicio)}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Hora fin</dt>
              <dd className="mt-1 text-gray-900">{formatHora(aplicacion.horafin)}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h3 className="font-semibold text-gray-900">Productos / Mezcla</h3>

          {productos.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">Sin productos registrados.</p>
          ) : (
            <>
              <div className="mt-4 hidden overflow-hidden rounded-xl border border-gray-100 sm:block">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Nombre comercial</th>
                      <th className="px-4 py-2 font-medium">Método</th>
                      <th className="px-4 py-2 font-medium">Dosis / ha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {productos.map((producto) => (
                      <tr key={producto.id}>
                        <td className="px-4 py-2 text-gray-900">{producto.nombrecomercial || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {METODO_LABELS[producto.metodo] || producto.metodo || '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{producto.dosis_ha || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-col gap-4 sm:hidden">
                {productos.map((producto, index) => (
                  <div key={producto.id} className={index > 0 ? 'border-t border-gray-100 pt-4' : ''}>
                    <p className="font-medium text-gray-900">{producto.nombrecomercial || '—'}</p>
                    <p className="mt-1 text-sm text-gray-600">
                      {METODO_LABELS[producto.metodo] || producto.metodo || '—'}
                      {producto.dosis_ha ? ` · ${producto.dosis_ha} / ha` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export default FertilizanteDetailPage;
